import { useEffect, useState, useSyncExternalStore } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { projectNavTarget } from './project-nav-targets';
import { Users, Briefcase, ListTodo, ChevronsLeft, Star, Trash2, RotateCcw, X } from '../../icons';
import type { TeamDto } from '../../api/project-teams';
import { listTrashedProjects, restoreProject, purgeProject, type TrashedProjectDto } from '../../api/tasks';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import { formatDate } from '../../lib/base-i18n';
import { useFavoritesStore } from '../../store/favoritesStore';
import { groupFavorites, favoriteKind, type FavoriteKind } from '../../lib/favorite-kind';
import '../../components/Favorites/FavoriteGroups.css';

/**
 * ⭐ **«목적지»를 가진 열쇠 — ★ 목록이 «진실»이다** (2026-10-07 에 배열로 바꿨다).
 *
 * ## ⚠ 왜 배열인가 — ⛔ 유니온은 «런타임에 셀 수 없다»
 *
 * 종전에는 `'teams' | 'projects' | 'my-tasks'` 유니온이었다. ⇒ ★ 그러면 시험이
 * 「**모든** 열쇠가 제 목적지를 가졌나」를 ⛔ 못 센다(손으로 셋을 적어야 하고, 넷째가 생기면
 * 그 손 목록이 조용히 낡는다 — [[counted-by-eye-in-a-doc-others-verify]]).
 * ⇒ ★ 그래서 **배열을 진실로 두고 유니온을 거기서 뽑는다.** 시험은 이 배열을 돈다.
 *
 * ## ⛔⛔ 2026-10-10 — ★ 이 배열은 ⭐ «메뉴에 보이는 것»과 ★ 같지 ⭐ 않다
 *
 * `'home'` 은 ★ **메뉴에서 걷혔다**(판 D · 사용자 결정 「홈 지워」). ⇒ ⭐ 그런데 ★ 이 배열에는
 * **남아 있다** — ⇒ 까닭: `ProjectV2Route` 가 깃발이 꺼졌을 때 ⭐ «되돌아갈 곳»으로
 * `projectNavTarget('home')` 을 쓴다(그 파일 :72). ⇒ ⇒ ★ 즉 ⭐ «목적지»는 살아 있다.
 *   ⇒ ⚠ 뺐더니 ★ 타입이 좁아져(`ProjectNavKey = typeof …[number]`) ⇒ ⛔ 그 제품 코드가
 *     **TS2345** 로 깨졌다(2026-10-10 탐침: typecheck EXIT=2 · 오류 셋). ⇒ 그래서 ★ 남긴다.
 *
 * ## ⛔⛔ 그리고 ★ 여기 있던 ⭐ «거짓 문장»을 지웠다
 *
 * 종전 줄 ⇒ 「둘이 어긋나면 「메뉴에 있는데 안 보인다」가 된다. ★ **그래서 시험이 둘을 맞댄다.**」
 * ⇒ ⛔ 그 마지막 절이 ★ ⭐ «거짓»이었다 — ⇒ 2026-10-10 전수:
 *   `PROJECT_NAV_KEYS` 를 쓰는 곳 ★ 넷 중 ⇒ `items` 와 **맞대는** 것은 ⭐ **0 건**이다
 *   (`project-nav-targets.test.ts` 는 «목적지가 라우터에 있나·서로 다른가»만 잰다).
 * ⇒ ⇒ ⭐ 즉 ★ 「지키겠다는 **뜻**」을 ⇒ 「**있다**」로 적은 것이다.
 *   ⇒ ⇒ ★ 어제 내가 「additive 라는 **뜻**」을 「지운 줄 0 이라는 **수**」로 적은 것과 ⭐ 같은 꼴이다.
 *
 * ⏳ **만료 조건 둘**
 *   ① 「메뉴에 없는 열쇠」가 ★ **둘**이 되는 날 ⇒ ⭐ 이 배열을 가른다 —
 *      `PROJECT_NAV_TARGET_KEYS` / `PROJECT_NAV_ITEM_KEYS`.
 *      ⇒ ⛔ **지금 가르지 않는다** — 하나('home')일 때 가르면 ★ 이름만 둘로 늘고 ⭐ 얻는 것이 없다.
 *   ② `PROJECT_NAV_KEYS` ⇔ `items` 를 ★ **맞대는 가드가 ⭐ 없다**(위 전수 0건).
 *      ⇒ ⚠ 이 판에서 **세우지 않았다** — 판 D 의 범위는 「홈 지우기」이고 ⇒ 가드를 늘리는 것은
 *        ★ 다른 판이다. ⇒ ⭐ 그러니 ★ 세우는 사람이 ⇒ **이 줄을 지워라.**
 */
export const PROJECT_NAV_KEYS = ['home', 'teams', 'projects', 'my-tasks'] as const;

export type ProjectNavKey = typeof PROJECT_NAV_KEYS[number];

/**
 * 펼침 대상 키 — 라우트로 가는 ProjectNavKey 에 '즐겨찾기'·'휴지통'을 더한 것.
 * 즐겨찾기·휴지통은 이동할 전용 화면이 없고 목록을 인라인으로 펼치기만 하므로,
 * 라우팅용 ProjectNavKey 를 오염시키지 않고 여기서만 넓힌다.
 *
 * ⚠ **'trash' 를 `PROJECT_NAV_KEYS` 에 넣지 않은 까닭** (2026-10-10) —
 *   그 배열은 「**제 목적지(라우트)를 가진** 항목」의 진실이고, `project-nav-targets.ts`
 *   가 그 배열을 돌며 「모든 열쇠가 목적지를 가졌나」를 잰다.
 *   ⇒ ⛔ 휴지통은 ★ 갈 화면이 «없다»(펼침 전용) ⇒ 거기 넣으면 그 시험이 ★ 없는 목적지를
 *     찾다가 빨개진다 — 그리고 그 빨강은 ⭐ «결함이 아니라» ★ 내가 분류를 틀린 것이다.
 *   ⇒ ✅ 그래서 즐겨찾기와 ★ 같은 자리(이 유니온)에 둔다 — ⇒ 선례를 그대로 따른다.
 */
export type ProjectNavExpandKey = ProjectNavKey | 'favorites' | 'trash';

/**
 * ⭐ **들어올 때 «펼쳐진 채»로 시작하는 항목** (2026-09-30 사용자 요구: 「하위 메뉴가 기본 오픈」).
 *
 * ⛔ 종전에는 `ProjectPage` 안에 `useState('teams')` 로 «글자»만 박혀 있었다. 네비를 홈 밖에도
 *   두면 그 글자가 두 곳이 되고, 「기본 오픈」을 고치려 할 때 «한 곳만» 고치게 된다.
 *   ⇒ ★ 그래서 요구사항을 «이름»으로 만들어 한 곳에 둔다 — 바꿀 자리가 여기 하나다.
 *
 * ⚠ 이 네비는 «한 번에 하나»만 펼친다(`expanded === key` 단일값). 그래서 「하위 메뉴를 «전부»
 *   기본 오픈」은 이 상수로는 안 되고 **상태를 집합으로 바꿔야 한다** — 그것은 홈의 동작도
 *   함께 바뀌므로 이 판의 일이 아니다(사용자 확인 대기).
 *   · 만료 조건 — 「전부 열림」으로 정해지면 이 상수는 «집합»이 되고 `expanded` 도 그렇게 바뀐다.
 */
export const PROJECT_NAV_DEFAULT_EXPANDED: ProjectNavExpandKey = 'teams';

interface ProjectHomeNavProps {
  /** 펼쳐진 항목 — '스페이스' 면 팀 목록이, '즐겨찾기' 면 즐겨찾기 목록이 인라인으로 나온다. */
  expanded: ProjectNavExpandKey | null;
  onToggle: (key: ProjectNavExpandKey) => void;
  /** 내 팀 목록 — expanded==='teams' 일 때만 렌더. */
  teams: TeamDto[];
  onOpenNav: (key: ProjectNavKey) => void;
  onOpenTeam: (teamId: string) => void;
  onCollapse?: () => void;
  /**
   * ⭐ 휴지통에서 프로젝트를 되돌린 뒤 (정본 2026-09-07 · 우리 이식 2026-10-10).
   * 넘기지 않으면 **목록만 줄고** 오른쪽 격자는 다음 로딩 때 반영된다 — 있으면 곧바로 다시 읽는다.
   *
   * ## ⛔ 우리는 ★ 아직 «안 넘긴다» — ⇒ ⭐ 그 까닭을 적는다
   *
   * 정본은 `ProjectPage.tsx` 두 자리(:199 · :281)에서 `onRestored={refreshLists}` 로 넘긴다.
   * ⇒ ⛔ 그런데 ★ 우리 `ProjectPage.tsx` 에는 ⭐ `refreshLists` 가 **없다**(실측 2026-10-10:
   *   우리에 있는 것은 `refreshSummary` · `refreshMyTasks` 둘이다).
   *   ⇒ ⇒ 정본의 그 함수는 `onProjectDeleted` · `onSpaceDeleted` 같은 ★ «정본 전용» 기능과
   *      묶여 태어난 것이라, 우리 쪽에 그대로 있는 것이 아니다.
   * ⇒ ⇒ ⭐ 즉 「어느 목록을 다시 읽을 것인가」는 ★ 베끼기가 아니라 ⭐ «판단»이고,
   *   ⇒ ⛔ 그리고 `ProjectPage.tsx` 는 ★ 이 판의 허용 목록 «밖»이다.
   *   ⇒ ✅ 그래서 **칸은 열어 두고 붙이지 않았다** — ⇒ 되돌리기 자체는 ★ 다 돈다
   *     (⇒ 그 줄이 목록에서 사라진다 · 서버에 복원이 걸린다).
   *
   * ⚠ **만료 조건** — `ProjectPage.tsx` 를 건드려도 되는 판이 오면 그때 붙인다.
   *   ⇒ 트리거 ⇒ 이 글의 「우리에 `refreshLists` 가 없다」가 ★ 거짓이 되는 날
   *     (⇒ 누가 그 이름을 만들면 ⇒ ★ 여기 붙이는 것이 ⭐ 한 줄이 된다).
   *
   * ⛔⛔ **그래서 가드는 「주는 곳이 0」을 ★ 단언하지 «않는다».**
   *   ⇒ 그것을 단언하면 ⇒ ★ 누가 ⭐ «옳게» 붙이는 날 ⇒ ⛔ 가드가 ★ 그 옳은 변경을 막는다 —
   *     ⇒ ⇒ 내가 D-day 가드에서 이미 밟은 함정이다(`bare === true` 닻이 정당한 고침을 막았다).
   *   ⇒ ✅ 가드가 잠그는 것은 ★ 「**불렸다면** ⭐ 복원이 성공한 뒤여야 한다」다 —
   *     ⇒ ⇒ 그것은 ★ 붙이든 안 붙이든 ⭐ 늘 참이어야 하는 규칙이라 ⇒ 미래를 막지 않는다.
   */
  onRestored?: () => void;
}

/**
 * ProjectHomeNav (2026-07-25) — 3c 프로젝트 홈의 **좌측 2차 네비**.
 * 레이아웃 SSOT: `docs/all-frame.html` 05.프로젝트 3c —
 *   "좌측 2차 사이드에서 **내 팀** 선택 시 내 팀들이 나열되고, 상세는 컨텐츠 영역에."
 *
 * 2026-08-18 — 상사 리뷰 반영으로 항목이 넷이 됐다:
 *   스페이스 · 내 프로젝트 · 내 할일 · 즐겨찾기
 *   - '스페이스' 는 기존 '내 팀' 의 라벨만 바뀐 것이다(key='teams', 동작·목적지 그대로).
 *     ⭐ 2026-10-08 사용자 결정 ⓐ — '작업공간(가제)' 의 «(가제)» 를 걷고 ★ 이름을 확정했다.
 *       ⇒ ⛔ 열쇠 이름(nav_workspace)은 ★ 그대로다 — 값만 바뀌었다(project-i18n 의 그 머리말).
 *     통계 카드·위젯이 쓰는 '내 팀'(project.stats.teams)은 건드리지 않았다.
 *     ⭐ 2026-10-12 정정 — 사용자가 「통계카드,위젯이 쓰는 내팀 도 수정해줘」라고 해
 *       ⇒ 그 열쇠의 ko 를 '내 스페이스' 로 바꿨다. ⇒ 위 줄은 그 시점의 사실이다.
 *       ⇒ ⛔ 그러니 이것을 「안 건드린다던 것을 건드렸다」로 읽지 말 것 — 사용자 결정이다.
 *       ⇒ ⚠ 열쇠 이름(project.stats.teams)은 그대로다 — 값만 바뀌었다.
 *   - '즐겨찾기' 는 ⑤ 우측 레일이 쓰는 favoritesStore 를 그대로 읽어 인라인으로 펼친다
 *     (신규 API·저장소 0 — 레일에서 등록한 것이 여기에 같이 보인다).
 */
export function ProjectHomeNav({
  expanded, onToggle, teams, onOpenNav, onOpenTeam, onCollapse, onRestored,
}: ProjectHomeNavProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  /*
   * ⭐ 2026-10-13 사용자 결정 — 프로젝트 메뉴의 즐겨찾기는 «프로젝트 즐겨찾기»다.
   *   ⇒ 스페이스·프로젝트 두 갈래만 보인다. 1차 메뉴(채팅·메일·외부 링크 …)는 ★ 우측 레일
   *     즐겨찾기(FavoritesPopover)의 몫이라 여기서 뺀다 — 저장소·저장 형식은 그대로(화면만 거른다).
   *   ⇒ 덤: 레일 팝오버는 canSeeMenuPath 로 권한 밖 메뉴를 거르는데 이 자리는 그 거르기가 없었다
   *     (결정 문서 §3①) ⇒ 메뉴 갈래를 빼면 그 구멍도 이 자리에선 닫힌다.
   *   ⇒ 개수 배지 · 빈 상태 · 갈래 머리글이 모두 «이 목록»을 본다(셋이 같은 수를 말한다).
   */
  const favorites = useFavoritesStore((s) => s.items)
    .filter((f) => favoriteKind(f) !== 'menu');
  const removeFavorite = useFavoritesStore((s) => s.remove);

  /**
   * ⭐ **스페이스 즐겨찾기의 이름은 ★ «지금 팀 목록»을 우선한다** (정본 2026-08-21 · 우리 이식 2026-10-10).
   *
   * ## ⛔ 무엇이 고쳐지나 — ⇒ ★ 이름을 바꾸면 ⭐ «옛 이름»이 남았다
   *
   * `favoritesStore` 에 담기는 `label` 은 ★ **등록 시점의 값**이다. ⇒ ⛔ 그래서 스페이스 이름을
   * 바꾸면 ⇒ ⭐ 즐겨찾기 목록에는 ★ 바꾸기 «전» 이름이 그대로 서 있었다.
   *   ⇒ ⇒ ★ 그리고 ⭐ 누르면 «새 이름의 화면»이 열린다 — ⇒ ⛔ 목록과 화면이 어긋난다.
   *
   * ⇒ ✅ 레일 팝오버가 메뉴 라벨을 «카탈로그»로 갱신하는 것과 ★ 같은 취지다.
   *   ⇒ ⭐ 여기서는 카탈로그 대신 ★ **이미 들고 있는 `teams`** 를 쓴다 ⇒ ⇒ 추가 조회 ⭐ 0.
   * ⇒ ⚠ 목록에 «없는» 항목은 ★ 저장된 `label` 그대로다 — ⇒ ⭐ 지워진 스페이스가 ★ 빈 칸이 되지 않게.
   * ⇒ ⚠ 그리고 ★ `store` 를 ⭐ «고쳐 쓰지 않는다» — ⇒ 저장 형식 무변경 · 마이그 ⭐ 0.
   *
   * ⏳ **만료 조건** — 프로젝트 즐겨찾기(`/task?project=`)도 ★ 같은 문제를 갖는다
   *   (⇒ 프로젝트 이름을 바꾸면 옛 이름이 남는다). ⇒ ⛔ 이 판에서 ★ 안 고쳤다 —
   *   ⇒ ⇒ 까닭: ⭐ 이 컴포넌트는 `projects` 를 ★ **안 받는다**(`teams` 만 받는다).
   *   ⇒ ⇒ ⇒ ★ 그것을 받게 하려면 ⭐ 호출자 둘(`ProjectPage` · `ProjectNavShell`)이 바뀐다
   *      ⇒ ⇒ ★ 그래서 ⭐ «다른 판»이다. ⇒ 그 판이 오면 ★ 이 함수를 ⭐ 넓힌다.
   */
  const liveLabel = (to: string, fallback: string): string => {
    const m = /^\/project\/space\/(.+)$/.exec(to);
    if (!m) return fallback;
    return teams.find((t) => t.id === m[1])?.name ?? fallback;
  };

  /*
   * ⭐ 갈래별로 묶어 ★ 머리글을 세운다 (정본 2026-09-04 사용자 지시 · 우리 이식 2026-10-10).
   *
   * ⛔ 종전에는 스페이스·프로젝트·레일 메뉴가 ★ 이름만 ⭐ 한 줄로 섞여 나왔다 —
   *   ⇒ '승인 흐름' 이 ★ 스페이스인지 프로젝트인지 ⇒ ⭐ 목록만 봐서는 알 수 없어 ★ 눌러 봐야 알았다.
   * ⇒ ⭐ 판정은 `lib/favorite-kind` 가 ★ «경로»로만 한다 ⇒ ⇒ store 무변경 · 마이그 ⭐ 0.
   *   ⇒ ⚠ 까닭: ★ 등록하는 쪽(FavoriteStar)이 ⭐ 화면마다 다른 꼴의 경로를 담으므로
   *     ⇒ ⇒ ★ 그 경로가 ⭐ 이미 갈래를 «말하고 있다».
   */
  const groups = groupFavorites(favorites);
  const groupLabel: Record<FavoriteKind, string> = {
    space: tProject('project.home.fav_group_space'),
    project: tProject('project.home.fav_group_project'),
    menu: tProject('project.home.fav_group_menu'),
  };

  /*
   * ⭐ 휴지통 (정본 2026-09-07 · 우리 이식 2026-10-10) — ★ «펼칠 때» 읽는다.
   *
   * 프로젝트 홈이 뜰 때마다 부르면 대부분 열어 보지도 않을 목록 때문에 요청이 한 번 더
   * 나가므로, ★ 연 사람만 값을 치른다. 그래서 항목에 ⭐ 개수 배지도 달지 않는다
   * (즐겨찾기는 store 라 공짜지만 이건 아니다).
   *
   * ⚠ `trash === null` 은 ★ 「아직 안 읽었다」이고 `[]` 는 ★ 「읽었고 비었다」다 —
   *   ⇒ ⛔ 둘을 섞으면 「불러오는 중…」이 ★ 영영 서 있거나 ⇒ 「비어 있어요」가 ★ 먼저 번쩍인다.
   */
  const [trash, setTrash] = useState<TrashedProjectDto[] | null>(null);
  const [trashErr, setTrashErr] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  /*
   * ⭐⭐ 완전 삭제 (2026-10-10 사용자 결정 · 단추 2026-10-12) — ⇒ ⛔ 되돌릴 수 없다.
   *
   * ## ⚠ 「만든 사람만」을 ★ 여기서 ⭐ 재지 «않는» 까닭 — ⇒ ★ 목록이 ⭐ 이미 그것이다
   *
   * ```
   * 서버 projects.routes.ts:767 (GET /api/v1/projects/trash 의 where)
   *   ...(isAdmin ? {} : { createdBy: userId })
   *   ⇒ 그 줄 주석 ⇒ 「빼면 ★ 남이 지운 프로젝트가 ⭐ 내 휴지통에 보인다」
   * ⇒ ⇒ ✅ 즉 비관리자에게는 ★ «자기가 만든 것»만 온다 ⇒ `isProjectCreator(p)` 가 늘 참이다
   * ⇒ ⇒ ✅ 관리자는 기관 전체가 보이는데 ⇒ purge 의 createdBy 검사를
   *      `effective === 'ADMIN_BYPASS'` 가 면제한다(projects.routes.ts:917) ⇒ 그들도 지울 수 있다
   * ⇒ ⇒ ⇒ ⭐ 그래서 ★ 「이 목록에 보이는 행 = 보는 사람이 지울 수 있는 행」이다
   *
   * ⇒ 그리고 ★ 바로 옆 ⭐ 「되살리기」 단추도 ★ 조건이 없다(:489) — ⇒ 복원과 완전 삭제의
   *   권한이 ⭐ «같은 규칙»이기 때문이다(projects.routes.ts:868 「새 매트릭스를 만들지 않는다」)
   *   ⇒ ⇒ 두 단추에 다른 조건을 달면 ★ 「같은 뜻을 다른 글자로」가 된다
   * ```
   *
   * ## ⛔⛔ 그래서 ★ 이 화면의 옳음이 ⇒ ⭐ «서버의 where 한 줄»에 ★ 매달린다
   *
   * ```
   * ⇒ 누가 그 :767 을 지우면 ⇒ ★ 남이 지운 프로젝트가 내 휴지통에 서고
   *   ⇒ ⇒ 그 옆에 ★ «완전 삭제» 단추가 ⭐ 함께 선다 (⇒ 서버가 403 으로 막기는 한다)
   * ⇒ ✅ 그러니 가드가 ★ «서버 소스를 읽어» 그 줄을 못 박는다
   *      ⇒ project-purge.guard.test.ts ⓐ (⇒ 대조군: 그 글자를 덮으면 울리나)
   * ```
   *
   * ## ⏳ 남은 좁은 틈 — ★ 만든 뒤 ⭐ OWNER 를 «넘긴» 사람
   *
   * ```
   * ⇒ projects.routes.ts:48 이 「마지막 OWNER 보호」를 적었으니 ⇒ OWNER 가 둘일 수 있고
   *   ⇒ ⇒ 만든 사람이 MANAGER 로 내려갈 수 있다
   * ⇒ ⚠ 나는 그것을 ⛔ 직접 재지 «않았다» — 주석으로 읽은 것이다(관제도 같다)
   * ⇒ ✅ 그 경우 ⇒ 목록엔 보이고 ⇒ 403 FORBIDDEN 이 온다
   *      ⇒ ⇒ 그 자리를 `project.home.purge_err_forbidden` 이 그린다(아래 onPurge)
   * ```
   */
  const [purgeTarget, setPurgeTarget] = useState<TrashedProjectDto | null>(null);
  const [purgeName, setPurgeName] = useState('');
  const [purging, setPurging] = useState(false);
  const [purgeErr, setPurgeErr] = useState<'name' | 'forbidden' | 'other' | null>(null);

  useEffect(() => {
    if (expanded !== 'trash') return;
    let alive = true;
    setTrashErr(false);
    void listTrashedProjects()
      .then((rows) => { if (alive) setTrash(rows); })
      .catch(() => { if (alive) { setTrash([]); setTrashErr(true); } });
    return () => { alive = false; };
  }, [expanded]);

  /**
   * ⚠ 입력한 글자를 ★ «그대로» 보낸다 — ⇒ ⛔ trim 하지 않는다.
   *   서버가 `confirmName !== project.name` 로 재므로(projects.routes.ts:924),
   *   여기서 다듬으면 ★ 「화면은 보냈다는데 서버는 다르다」가 된다.
   *   ⇒ 단추를 살리는 조건도 같은 글자로 잰다(아래 disabled).
   *
   * ⚠ 400 은 ★ 관문 ⭐ «맨 뒤»다(projects.routes.ts:919 — 먼저 재면 존재가 샌다).
   *   ⇒ ✅ 그래서 400 을 보는 사람은 이미 권한이 있다 ⇒ 「이름을 다시」로 그려도 정보가 안 샌다.
   */
  const onPurge = async () => {
    const p = purgeTarget;
    if (p == null || purging) return;
    setPurging(true);
    setPurgeErr(null);
    try {
      await purgeProject(p.id, purgeName);
      setTrash((prev) => (prev == null ? prev : prev.filter((r) => r.id !== p.id)));
      setPurgeTarget(null);
      setPurgeName('');
      onRestored?.();
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } })?.response?.data?.code;
      setPurgeErr(code === 'CONFIRM_NAME_MISMATCH' ? 'name'
        : code === 'FORBIDDEN' || code === 'FORBIDDEN_NOT_CREATOR' ? 'forbidden' : 'other');
    } finally {
      setPurging(false);
    }
  };

  const onRestore = async (p: TrashedProjectDto) => {
    setRestoring(p.id);
    try {
      await restoreProject(p.id);
      // 되돌린 것만 목록에서 뺀다 — 전체를 다시 읽지 않아 목록이 깜빡이지 않는다.
      setTrash((prev) => (prev ?? []).filter((r) => r.id !== p.id));
      onRestored?.();
    } catch {
      setTrashErr(true);
    } finally {
      setRestoring(null);
    }
  };

  const items: Array<{ key: ProjectNavExpandKey; label: string; icon: typeof Users }> = [
    /*
     * ⛔⛔ **2026-10-10 — ★ 「홈」 항목을 ⭐ 걷었다**(사용자 결정 「홈 지워」 · 관제 경유).
     *
     * ⚠ 이 자리에 2026-10-07 에 더한 「홈」이 있었다. ⇒ ⭐ 그 판의 까닭은
     *   「이 메뉴에 `/project` 로 가는 항목이 ★ 0 이었다」였다.
     * ⇒ ⭐ 걷어도 ★ 홈으로 가는 길은 ⭐ **남는다** — ⇒ 2026-10-10 ★ 실측(제품 1,171 파일을 훑어):
     *
     *     `/project` 로 «가는» 제품 파일        ⇒ ⭐ **5**
     *       DetailPageHeader.tsx (Link) · TeamsDetailPage.tsx (navigate) ·
     *       ProjectV2Route.tsx (helper) · SpaceV2Page.tsx (navigate) · ProjectSidePanel.tsx (a href)
     *     그중 `DetailPageHeader` 를 «그리는» 화면 ⇒ ⭐ **7**
     *       MyTasksDetailPage · ProjectSearchPage · ProjectsDetailPage · StatsDetailPage ·
     *       TeamsDetailPage · ChecklistTemplatesV2Page · MyTasksBoard
     *
     *   ⇒ ⇒ ★ 그래서 ⭐ «갈 길이 없어지는» 것이 ⛔ 아니다.
     * ⚠⚠ **정정** — 이 줄의 첫 판에 「★ 여덟 곳 · DetailPageHeader 가 ⭐ 다섯 화면」이라 적었는데
     *   ⇒ ⛔ 그 수는 ★ 내가 ⭐ **되재지 않은** 것이었다(관제 쪽지에서 받은 「8곳」을 그대로 옮겼다).
     *   ⇒ ✅ 재서 고쳤다 ⇒ ★ 제품 파일 ⭐ 5 · DetailPageHeader 를 쓰는 화면 ⭐ 7.
     *   ⇒ ⇒ ⭐ 그리고 ★ 그 수를 ⭐ 손으로 들고 있지 않도록 ⇒ ★ `project-nav-home-removed.guard`
     *     ④ 가 ⭐ **직접 센다**(⇒ 「여럿인가」만 단언하고 ★ 수는 보고가 적는다).
     * ⭐⭐ 2026-10-12 정정 — 사용자가 ★ 그 갈음의 «값»을 겪었다.
     *   ⇒ 사용자 원문 ⇒ 「프로젝트 하위메뉴에서 스페이스를 누르면 /project 메뉴가 안간다.
     *     홈으로 안간다. 언제 수정하나?」
     *   ⇒ 결정 ⓐ ⇒ 「홈」 항목은 ★ 그대로 두고 ⇒ ⭐ 「스페이스」 클릭에 ★ 홈 이동을 더했다.
     *     ⛔ ⓑ(「홈」 항목 되살리기)는 ★ 안 골랐다 ⇒ ⇒ 2026-10-10 결정 「홈 지워」는 ⭐ 그대로 선다.
     *   ⇒ ⚠ 아래 「홈으로 가는 길은 다섯 곳에 남는다」는 ★ 틀리지 «않았지만»
     *     ⇒ ⇒ «2차 메뉴 안에서는» ⭐ 0 이었다 — ★ 그것이 이번에 드러난 자리다.
     *
     * ⇒ ⚠ 그리고 ★ `projectNavTarget('home')` 은 ⭐ **그대로 산다** —
     *   `ProjectV2Route` 가 깃발 꺼짐 때 되돌아갈 곳으로 쓴다. ⇒ ⇒ ★ 위 배열 머리말을 보라.
     *
     * ⚠⚠ **그리고 ★ 이것이 ⭐ «앞선 사용자 결정»을 덮는다는 것을 ★ 적어 둔다.**
     *   `project-home-link-words.test.ts` 머리말의 2026-10-07 결정 ⓑ 는 ★ 두 절이었다:
     *     ① 상세 머리말·측면 패널 ⇒ 「프로젝트 홈」      ⇐ ⭐ 이 절은 ★ 그대로 산다
     *     ② 2차 메뉴 «안»        ⇒ 「홈」              ⇐ ⛔ 이 절은 ★ 2026-10-10 에 ⭐ 다했다
     *   ⇒ ⛔ 그 파일이 「**하나로 줄이자로 되돌리지 마십시오**」라 적었는데 ⇒ ★ 이것은 ⭐ 그것이
     *     **아니다** — ⇒ 「두 글자를 하나로 합치기」가 아니라 ★ ⭐ «항목을 없애기»다.
     *   ⇒ ⇒ ★ 그래서 그 파일의 ⭐ **기록은 지우지 않고** ⇒ ② 절만 ★ 「다했다」로 적었다.
     */
    { key: 'teams', label: tProject('project.home.nav_workspace'), icon: Users },
    // 2026-07-25 — 라벨 '프로젝트' → '내 프로젝트', 목적지도 3d(/task)로.
    //   (구 /project/projects 목록 화면은 라우트만 남기고 메뉴 연결에서 제외)
    { key: 'projects', label: tProject('project.home.my_projects'), icon: Briefcase },
    { key: 'my-tasks', label: tProject('project.stats.my_tasks'), icon: ListTodo },
    { key: 'favorites', label: tProject('project.home.nav_favorites'), icon: Star },
    /*
     * ⭐ 휴지통 — ★ 즐겨찾기 «바로 아래»다 (정본 2026-09-07 사용자 지시 · 우리 이식 2026-10-10).
     * ⇒ ⚠ 차례가 뜻을 가진다 — ⭐ 되돌리기는 ★ «드물게» 찾는 일이라 맨 아래다.
     *   ⇒ ⇒ 위로 올리면 자주 쓰는 항목이 밀린다.
     */
    { key: 'trash', label: tProject('project.home.nav_trash'), icon: Trash2 },
  ];

  /*
   * ★ 「이미 /project 에 있나」 — 2026-10-12 결정 ⓐ 가 더한 이동이 «히스토리를 쌓지» 않게.
   *   ⇒ 주소는 `projectNavTarget('home')` 한 곳에서 온다(글자로 적지 않는다).
   *   ⚠ 끝 슬래시 차이로 어긋나지 않게 벗겨서 견준다(/project ⇔ /project/).
   */
  const { pathname } = useLocation();
  const homePath = projectNavTarget('home');
  const strip = (p: string) => (p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p);
  const atHome = strip(pathname) === strip(homePath);

  /** 펼침 전용(이동할 화면이 없는) 항목 — 클릭이 토글이어야 하는지 판별. */
  const isExpandable = (key: ProjectNavExpandKey): boolean =>
    key === 'teams' || key === 'favorites' || key === 'trash';

  return (
    <aside className="pj-home-nav scrollbar-thin" aria-label={tProject('project.title')}>
      <div className="pj-home-nav-head">
        <span className="pj-home-nav-title">{tProject('project.title')}</span>
        {onCollapse && (
          <button
            type="button"
            className="pj-teams-side-new"
            onClick={onCollapse}
            aria-label={tProject('project.home.nav_collapse')}
            title={tProject('project.home.nav_collapse')}
          >
            <ChevronsLeft size={14} aria-hidden />
          </button>
        )}
      </div>

      <ul className="pj-home-nav-list">
        {items.map(({ key, label, icon: Icon }) => (
          <li key={key}>
            <button
              type="button"
              className={`pj-home-nav-item${expanded === key ? ' pj-home-nav-item--on' : ''}`}
              /*
               * 스페이스·즐겨찾기는 눌러서 펼치고, 나머지는 곧장 그 화면으로 간다.
               *
               * ★ 2026-10-12 사용자 결정 ⓐ — 「스페이스」는 «펼치면서 /project 로도 간다».
               *   사용자 원문 ⇒ 「프로젝트 하위메뉴에서 스페이스를 누르면 /project 메뉴가
               *     안간다. 홈으로 안간다. 언제 수정하나?」
               *   ⇒ 세 안 중 ⓐ(가고 ＋ 펼친다)를 골랐다. ⓑ「홈」 항목 되살리기는 «안 골랐다».
               *   ⚠ 정본도 이 자리는 「펼치기만」이다 ⇒ 즉 이것은 «정본과 달라지는» 자리다.
               *     사용자 결정이므로 그대로 둔다(정본에 맞추지 말 것).
               *   ⛔ 즐겨찾기·휴지통은 건드리지 않았다 — 사용자가 「스페이스」만 말했다.
               *
               * ⚠ 같은 주소로 또 navigate 하면 «히스토리가 쌓인다»(react-router 는 같은
               *   경로에도 push 한다) ⇒ 뒤로 가기가 한 번씩 더 필요해진다.
               *   ⇒ 그래서 «이미 그 자리면» 안 간다 — 아래 `atHome` 이 그것이다.
               */
              onClick={() => {
                if (key === 'teams') {
                  if (!atHome) onOpenNav('home');
                  onToggle(key);
                  return;
                }
                return isExpandable(key) ? onToggle(key) : onOpenNav(key as ProjectNavKey);
              }}
              aria-expanded={isExpandable(key) ? expanded === key : undefined}
            >
              <Icon size={14} aria-hidden />
              <span>{label}</span>
              {/*
                ⭐ **담긴 개수** (정본 2026-08-21 · 우리 이식 2026-10-10) — ★ 펼치지 않아도 보이게.
                ⇒ ⭐ 0 이면 ★ 배지를 ⭐ «아예 안 그린다» — ⇒ ⛔ 「0」 배지는 ★ 정보가 아니라 ⭐ 잡음이다.

                ⚠ `aria-hidden` 인 까닭 ⇒ ★ 이 배지가 ⭐ «접근성 이름»에 섞이면
                  ⇒ ⇒ ⛔ 항목 이름이 ★ 「즐겨찾기 3」처럼 ⭐ 개수에 따라 «흔들린다».
                  ⇒ ⇒ ⇒ ★ 그러면 읽어 주는 쪽이 ⭐ 「즐겨찾기」를 ★ 못 찾고,
                     ⇒ ⇒ 우리 시험들도 ★ `getByRole('button', { name: '즐겨찾기' })` 로 ⭐ 못 찾는다.
                  ⇒ ⇒ ⇒ ⭐ 개수는 ★ 펼친 목록이 ⭐ 이미 말해 준다(fav-grp-n 가 갈래별로도 센다).

                ⛔⛔ **휴지통에는 ★ 일부러 ⭐ 안 단다** — ⇒ 정본의 그 가름을 그대로 쓴다:
                  ⇒ 즐겨찾기 개수는 ★ `store` 에서 오므로 ⭐ «공짜»다
                  ⇒ ⛔ 그런데 휴지통 개수는 ★ ⭐ «서버를 한 번 더 불러야» 나온다
                     ⇒ ⇒ ★ 그러면 ⭐ 「연 사람만 값을 치른다」가 ★ 깨진다(판 A 의 그 결정).
                  ⇒ ⇒ ⇒ ⏳ 만료 조건 ⇒ ★ 서버가 ⭐ 홈 요약(`getProjectHomeSummary`)에
                     ⇒ ⇒ ★ «지운 프로젝트 수»를 ⭐ 함께 주는 날 ⇒ ★ 그때는 ⭐ 공짜가 되므로 ★ 달 수 있다.
              */}
              {key === 'favorites' && favorites.length > 0 && (
                <span className="pj-home-nav-count" aria-hidden>{favorites.length}</span>
              )}
            </button>

            {key === 'teams' && expanded === 'teams' && (
              <ul className="pj-home-nav-sub">
                {teams.length === 0 ? (
                  <li className="pj-home-nav-subempty">{tProject('project.team.empty')}</li>
                ) : (
                  teams.map((t) => (
                    <li key={t.id}>
                      <button type="button" className="pj-home-nav-subitem" onClick={() => onOpenTeam(t.id)}>
                        {t.name}
                      </button>
                    </li>
                  ))
                )}
                {/*
                  ⛔ 「팀 전체 보기」 항목은 ★ 걷었다 (2026-10-12 사용자 ⇒ 「확인하여 수정하라」).

                  사용자 원문 ⇒ 「하위 메뉴의 스페이스 메뉴에 내 스페이스가 안보이도록 했는데
                                 ⇒ 팀전체보기부터 다보인다. 정본과 다른듯하다. 확인하여 수정하라」

                  ★ 정본은 이 항목을 «안 그린다» ⇒ ref dd05a6fcd 의 이 파일 :181~195 가
                    `{key === 'teams' && …}` ⇒ `teams.map(...)` 으로 «끝난다».
                    ⇒ 그리고 열쇠 `project.home.all_teams` 는 정본에 «선언만» 있고 쓰이지 않는다
                      (정본 1 = 선언 · 우리 2 = 선언 ＋ 씀 — 걷은 뒤 우리도 1 이 된다).
                  ⛔ 되살리지 말 것.

                  ## 걷기 «전»에 센 것 — 「나가는 문」

                  이 항목이 `/project/teams` 로 가는 «유일한 길»인지 쟀다 ⇒ 아니다. 남는 길 둘:
                    ⓐ 검색 결과에서 스페이스를 누름 ⇒ ProjectSearchPage ⇒ `/project/teams?team=X`
                    ⓑ 스페이스 상세의 되돌아가기 ⇒ ProjectV2/SpaceV2Page (⇒ 라우터가 가리킨다)
                  ⚠ 그리고 `?team=X` 는 목록을 «숨기지 않는다» —
                    TeamsDetailPage 가 `<ul className="pj-team-cards">{teams.map(...)}` 로
                    목록을 늘 그리고, `?team=` 은 그중 하나를 «고르는» 것뿐이다
                    (지워진 id 면 teams[0] 으로 회복까지 한다).
                  ⛔ ProjectStatsCards 의 `/project/teams` 링크는 세지 않았다 —
                    그것은 3c 위젯 홈 전용이고, 깃발 AW-001 이 켜진 지금 안 그려진다.

                  ⏳ 만료 — 사용자가 「팀 목록으로 가는 «메뉴»가 필요하다」고 하면 그때 다시 쓴다.
                    ⇒ 그때 재야 할 것 ⇒ 위 ⓐⓑ 가 아직 사는가(둘 다 죽으면 입구가 0 이 된다).
                */}
              </ul>
            )}

            {/* 즐겨찾기 — ⑤ 우측 레일과 같은 favoritesStore 를 읽는다.
                등록은 스페이스 카드·스페이스 상세의 ☆(FavoriteStar), 해제는 ★ 여기 ✕ 로도 된다
                (2026-10-10 이식 전에는 ⇒ ⛔ 레일 팝오버에서«만» 다룰 수 있었다).
                외부 링크는 새 탭, 내부 경로는 NavLink — FavoritesPopover 와 같은 규칙.

                ⭐ 2026-10-10 — ★ 세 가지가 더해졌다(정본 dd05a6fcd):
                  ① 갈래 머리글(fav-grp) ⇒ 스페이스 · 프로젝트 · 메뉴
                  ② 행 오른쪽 ✕ (pj-home-nav-subdel) ⇒ ★ 여기서 해제
                  ③ liveLabel ⇒ ★ 스페이스 이름을 바꾸면 ⭐ «새 이름»이 보인다
                ⇒ ⚠ ★ 행(li)의 마크업은 ⭐ «종전과 같다» — ⇒ 이동 동작·CSS 가 그대로다.
                  ⇒ ⇒ 달라진 것은 ★ 사이에 ⭐ 머리글이 서고 ⇒ 행에 ✕ 가 붙는 것뿐이다.
                  ⇒ ⇒ ⭐ 되돌리려면 ★ 묶음 래퍼와 그 import 만 걷으면 ⇒ 평평한 목록으로 돌아간다.
                ⇒ ⛔ 개수 배지(pj-home-nav-count)는 ★ 안 더했다 — ⇒ ⭐ 그것은 «판 C» 다. */}
            {key === 'favorites' && expanded === 'favorites' && (
              <ul className="pj-home-nav-sub">
                {favorites.length === 0 ? (
                  <li className="pj-home-nav-subempty">
                    <span>{tProject('project.home.favorites_empty')}</span>
                    {/* 빈 상태에서 「그럼 어떻게 담지」로 끝나지 않게, 담는 방법을 바로 알려 준다. */}
                    <span className="pj-home-nav-subhint">
                      {tProject('project.home.favorites_empty_hint')}
                    </span>
                  </li>
                ) : (
                  groups.map((g) => (
                    <li key={g.kind} className="fav-grp">
                      <div className="fav-grp-head">
                        <span className="fav-grp-title">{groupLabel[g.kind]}</span>
                        <span className="fav-grp-n" aria-hidden>{g.items.length}</span>
                      </div>
                      <ul className="fav-grp-list">
                        {g.items.map((f) => {
                          const name = liveLabel(f.to, f.label);
                          return (
                            <li key={f.to} className="pj-home-nav-subrow">
                              {f.external ? (
                                <a
                                  className="pj-home-nav-subitem"
                                  href={f.to}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {name}
                                </a>
                              ) : (
                                <NavLink className="pj-home-nav-subitem" to={f.to}>
                                  {name}
                                </NavLink>
                              )}
                              {/* ⚠ 정본은 data-tip= 이다 — ⇒ 우리 트리엔 그것을 그리는 CSS 가 0 이라
                                  title= 로 둔다(판 A 에서 잰 그대로 · 이 파일의 다른 단추와도 같다). */}
                              <button
                                type="button"
                                className="pj-home-nav-subdel"
                                aria-label={tProject('project.home.favorites_remove', { name })}
                                title={tProject('project.home.favorites_remove', { name })}
                                onClick={() => removeFavorite(f.to)}
                              >
                                <X size={12} aria-hidden />
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  ))
                )}
              </ul>
            )}

            {/* 휴지통 (정본 2026-09-07 · 우리 이식 2026-10-10) — 지운 프로젝트를 되돌리는 자리.
                이름은 링크가 아니다: 지워진 프로젝트라 갈 곳이 없다. 되돌린 뒤라야 열 수 있다.
                ↺ 는 즐겨찾기의 ✕ 와 달리 늘 보인다 — 되돌리러 온 사람이 찾아야 하는 버튼이라
                hover 로 숨기면 없는 것처럼 보인다.

                ⭐⭐ 정본에서 ★ «벗어난» 한 곳 — ⛔ 정본은 `data-tip=` 을 쓴다.
                  ⇒ 재 보니(2026-10-10) 우리 트리에서 `data-tip` 을 ★ «그리는» CSS 가 ⭐ 0 이다:
                      git grep -l 'data-tip' 으로 apps/frontend/src 의 css 만 골라 ⇒ ★ 0 파일
                      (쓰는 tsx 는 14 파일이지만 ⇒ ★ 그것을 ⭐ 말풍선으로 만드는 규칙이 없다)
                      ⚠ 그 명령의 glob 을 여기 그대로 적지 않는다 — ⛔ 그 안의 별·슬래시가
                        ★ 이 JSX 주석을 ⭐ «닫는다»(실제로 TS1109 넷으로 죽었다).
                  ⇒ ⇒ ⛔ 즉 정본대로 두면 ★ 마우스를 올려도 ⭐ «아무것도 안 뜬다».
                  ⇒ ✅ 그래서 `title=` 로 둔다 — ⇒ 브라우저가 ★ 스스로 그려 주고,
                     ⇒ ⭐ 이 파일의 접기 단추(:194)가 ★ 이미 `title=` 이라 ⇒ 양식도 맞는다.
                  ⇒ ⚠ `aria-label` 은 ★ 그대로 뒀다 — 읽어 주는 쪽은 `title` 이 아니라 그것을 본다. */}
            {key === 'trash' && expanded === 'trash' && (
              <ul className="pj-home-nav-sub">
                {trash === null ? (
                  <li className="pj-home-nav-subempty">{tProject('project.home.trash_loading')}</li>
                ) : trash.length === 0 ? (
                  <li className="pj-home-nav-subempty">
                    <span>
                      {trashErr ? tProject('project.home.trash_err') : tProject('project.home.trash_empty')}
                    </span>
                    {!trashErr && (
                      <span className="pj-home-nav-subhint">
                        {tProject('project.home.trash_empty_hint')}
                      </span>
                    )}
                  </li>
                ) : (
                  <>
                    {/* 되돌리기가 실패해도 목록은 그대로 두고 줄 하나로만 알린다. */}
                    {trashErr && (
                      <li className="pj-home-nav-subempty pj-trash-err">
                        {tProject('project.home.trash_restore_err')}
                      </li>
                    )}
                    {trash.map((p) => (
                      <li key={p.id} className="pj-home-nav-subrow pj-trash-row">
                        <span className="pj-trash-name" title={p.name}>
                          <span className="pj-trash-nm">{p.name}</span>
                          <span className="pj-trash-meta">
                            {tProject('project.home.trash_meta', {
                              date: formatDate(p.deletedAt, p.deletedAt),
                              n: String(p.taskCount),
                            })}
                          </span>
                        </span>
                        <button
                          type="button"
                          className="pj-home-nav-subrestore"
                          disabled={restoring === p.id}
                          aria-label={tProject('project.home.trash_restore', { name: p.name })}
                          title={tProject('project.home.trash_restore', { name: p.name })}
                          onClick={() => void onRestore(p)}
                        >
                          <RotateCcw size={12} aria-hidden />
                        </button>
                        {/* ★ 완전 삭제 — ⇒ 조건 없이 그린다(위 purgeTarget 머리말의 까닭 셋) */}
                        <button
                          type="button"
                          className="pj-home-nav-subpurge"
                          aria-label={tProject('project.home.trash_purge', { name: p.name })}
                          title={tProject('project.home.trash_purge', { name: p.name })}
                          onClick={() => { setPurgeTarget(p); setPurgeName(''); setPurgeErr(null); }}
                        >
                          <Trash2 size={12} aria-hidden />
                        </button>
                      </li>
                    ))}
                  </>
                )}
              </ul>
            )}
          </li>
        ))}
      </ul>

      {/*
        ★ 완전 삭제 확인 — ⇒ ⭐ 사용자 조건 ★ 둘이 ⭐ 이 안에 있다 (까닭은 서버 소스가 들고 있다):
          ① ⭐ 「이름을 ★ 직접 입력」 ⇐ projects.routes.ts:134 (2026-10-10 사용자 결정)
             ⇒ ⚠ 붙여넣기를 ⛔ 막지 않는다 — ★ 그 조건은 ⭐ 없다(관제 확인)
          ② ⭐ 「첨부한 파일은 ★ 지워지지 않습니다」 ⇐ projects.routes.ts:862 (사용자 결정)
             ⇒ ⛔ 「고아가 쌓인다」·「걷는 자가 없다」는 ★ 쓰지 않는다 — ⇒ ⭐ 그것은 우리 사정이다
        ⚠ 단추를 살리는 조건은 ★ 서버 판정과 ⭐ «같은 글자»로 잰다(trim 하지 않는다) —
          ⇒ 다듬으면 「화면은 됐다는데 서버는 400」이 된다(projects.routes.ts:924).
        ⚠ 그래도 ★ 서버가 ⭐ 다시 본다 ⇒ :136 「화면만 믿으면 ★ 다른 길로 지워진다」.
      */}
      {purgeTarget != null && (
        <div className="pj-purge-back" role="presentation" onClick={() => { if (!purging) setPurgeTarget(null); }}>
          <div
            className="pj-purge-modal"
            role="dialog"
            aria-modal="true"
            aria-label={tProject('project.home.purge_title')}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="pj-purge-ttl">{tProject('project.home.purge_title')}</h2>
            <p className="pj-purge-warn">{tProject('project.home.purge_warn', { name: purgeTarget.name })}</p>
            <p className="pj-purge-files">{tProject('project.home.purge_files_kept')}</p>
            <label className="pj-purge-lbl" htmlFor="pj-purge-input">
              {tProject('project.home.purge_confirm_label')}
            </label>
            <input
              id="pj-purge-input"
              className="pj-purge-input"
              value={purgeName}
              autoComplete="off"
              disabled={purging}
              onChange={(e) => { setPurgeName(e.target.value); setPurgeErr(null); }}
            />
            {purgeErr != null && (
              <p className="pj-purge-err" role="alert">
                {purgeErr === 'name' ? tProject('project.home.purge_err_name')
                  : purgeErr === 'forbidden' ? tProject('project.home.purge_err_forbidden')
                    : tProject('project.home.purge_err')}
              </p>
            )}
            <div className="pj-purge-foot">
              <button type="button" className="pj-purge-cancel" disabled={purging}
                onClick={() => { setPurgeTarget(null); setPurgeName(''); setPurgeErr(null); }}>
                {tProject('project.home.purge_cancel')}
              </button>
              <button
                type="button"
                className="pj-purge-go"
                disabled={purging || purgeName !== purgeTarget.name}
                onClick={() => void onPurge()}
              >
                {tProject('project.home.purge_submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
